import React from "react";
import { Updater } from "use-immer";

import { ColorError, IgRadio, IgTypography } from "@intelligems/ig-theme";
import {
  GaAccount,
  GaPropertySummary,
  GoogleGlobalSettingsDto,
} from "@intelligems/ig-types";
import { FormControlLabel, Grid, Skeleton } from "@mui/material";

import { useGetGaPropertyStreams } from "../../../../api/google/get-ga-property-streams";
import { SkeletonGroup } from "../../../../components/Utils/SkeletonGroup";

interface GoogleAnalyticsPropertyProps {
  enabled: boolean;
  account: GaAccount;
  property: GaPropertySummary;
  globalSettings: GoogleGlobalSettingsDto;
  setGlobalSettings: Updater<GoogleGlobalSettingsDto>;
}

export const GoogleAnalyticsProperty = ({
  enabled,
  account,
  property,
  globalSettings,
  setGlobalSettings,
}: GoogleAnalyticsPropertyProps) => {
  const propertyStreams = useGetGaPropertyStreams(enabled, {
    body: undefined,
    queryParams: undefined,
    pathParams: {
      accountId: account.account || "",
      propertyId: property.property || "",
    },
  });

  if (!propertyStreams.isSuccess) {
    return <SkeletonGroup lines={2} groupSx={{ marginBottom: "10px" }} />;
  }

  return (
    <Grid
      container
      item
      sx={{ marginLeft: "10px", marginBottom: "10px" }}
      key={property.property}
      direction={"column"}
    >
      <Grid item>
        <IgTypography igStyle={"semibold"}>
          Property: {property.displayName} (
          {(property.property || "").split("/").pop()})
        </IgTypography>
      </Grid>
      <Grid item sx={{ marginLeft: "10px" }}>
        {propertyStreams.isLoading ? (
          <Skeleton />
        ) : (propertyStreams.data.streams || []).length ? (
          (propertyStreams.data.streams || []).map((stream) => {
            if (stream.webStreamData?.measurementId && property.property) {
              return (
                <Grid item key={stream.name} alignItems={"center"}>
                  <FormControlLabel
                    value={stream.webStreamData?.measurementId}
                    control={
                      <IgRadio
                        checked={
                          globalSettings.ga?.measurementId ===
                          stream.webStreamData.measurementId
                        }
                        onClick={() =>
                          setGlobalSettings({
                            ga: {
                              propertyId: property.property!,
                              streamId: stream.name!,
                              measurementId:
                                stream.webStreamData!.measurementId!,
                              accountId: account.account!,
                            },
                          })
                        }
                      />
                    }
                    label={
                      <IgTypography igStyle={"semibold"}>
                        Data Stream:{" "}
                        <IgTypography>
                          {stream.displayName} (
                          {stream.webStreamData?.measurementId || ""})
                        </IgTypography>
                      </IgTypography>
                    }
                  />
                </Grid>
              );
            }
          })
        ) : (
          <IgTypography sx={{ color: ColorError.main }}>
            No Data Streams found.
          </IgTypography>
        )}
      </Grid>
    </Grid>
  );
};
